/**
 * Open a link into the portal on the profile of the thing it links to.
 *
 * An invoice email for a Kaleido class lands on `/invoices/:id`. Without this the page opens
 * under whichever profile the parent last used — often BHCS — so a Kaleido invoice sits under a
 * BHCS header and the dashboard one click away lists the wrong brand's classes. With it, the
 * page reports its brand and the profile follows, exactly as if the link had carried `?brand=`.
 *
 * **Only the page a visit landed on.** The same pages are reachable from inside the portal —
 * Payments and Students list both brands' rows — and opening one of those is not a request to
 * change profile. The provider owns which page that is; see `alignLandingBrand`.
 *
 * The counterpart of `useBrandScopedPage`, which handles the other direction: the parent
 * switching profile while on the page. Pages that belong to one brand usually want both.
 */

import { useEffect } from 'react';
import { useBrandProfile } from '../contexts/brand-profile';

/**
 * @param subjectBrand  The brand of the thing on screen — `programs.brand_key`. Undefined
 *                      while it loads, which defers the switch until it is known.
 */
export function useLandingBrand(subjectBrand: string | null | undefined): void {
    const { alignLandingBrand } = useBrandProfile();

    useEffect(() => {
        alignLandingBrand(subjectBrand);
    }, [alignLandingBrand, subjectBrand]);
}
